import { Topic, TopicFormData } from "../../core/topic/entities/Topic";
import { pb } from "../pocketbase/pocketbase";
import type { RepositoryInterface } from "../interfaces/RepositoryInterface";
import { topicPartRepository } from "./topicPartRepository";

const collection = "topics";

function relationIds(value: unknown): string[] {
  if (!value || !Array.isArray(value)) return [];
  return value.map((item) =>
    typeof item === "string" ? item : (item as { id: string }).id,
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRecordToTopic(record: any): Topic {
  const categoryExpanded = record.expand?.categoryId;
  const categoryIdResolved =
    typeof categoryExpanded === "object" && categoryExpanded?.id != null
      ? categoryExpanded.id
      : (record.categoryId ?? record.category_id ?? null);

  return {
    ...record,
    image: pb.files.getURL(record, record.image) ?? null,
    trainingId: record.trainingId ?? record.training_id,
    categoryId: categoryIdResolved,
    relatedTopicIds: relationIds(
      record.relatedTopicIds ?? record.related_topics,
    ),
    badgeId: record.badgeId ?? record.badge_id ?? null,
    topicParts: record.topicParts ?? [],
  };
}

export const topicRepository: RepositoryInterface<Topic, TopicFormData> = {
  async getAll(trainingId: string) {
    const records = await pb.collection(collection).getFullList({
      filter: `trainingId = "${trainingId}"`,
      sort: "rank",
    });

    return records.map(mapRecordToTopic);
  },

  async getOne(id: string) {
    const record = await pb.collection(collection).getOne(id.toString(), {
      expand: "categoryId,badgeId",
    });

    const topic = mapRecordToTopic(record);
    topic.topicParts = await topicPartRepository.getAll(id);
    return topic;
  },

  async addUpdate(data: TopicFormData, id: string | null) {
    const { image, ...rest } = data;
    const payload: Record<string, unknown> = { ...rest };

    if (image instanceof File) {
      payload.image = image;
    } else if (image === null || image === "") {
      payload.image = "";
    }

    let record;
    if (id) {
      record = await pb.collection(collection).update(id.toString(), payload);
    } else {
      record = await pb.collection(collection).create(payload);
    }

    return topicRepository.getOne(record.id);
  },

  async delete(id: string) {
    await pb.collection(collection).delete(id.toString());
  },

  async updateOrder(list: Topic[]) {
    const updates = list.map((topic, index) => ({
      id: topic.id,
      rank: index + 1,
    }));

    await pb.send("/api/reorder", {
      method: "POST",
      body: {
        collection,
        data: updates,
      },
    });
  },
};
